import { Form, Select } from 'antd';
import { Input, InputNumber } from 'antd5';
import { useTranslation } from 'react-i18next';

import { DatePicker } from 'components/ui';

import { type DeliveryModeArgumentOption } from './types';

const { Option } = Select;

type Props = {
  showHermesId?: boolean;
  showShareInHorizonOrganization?: boolean;
  showCostRates?: boolean;
  showDateFilter?: boolean;
  shareInHorizonOrganizations?: DeliveryModeArgumentOption[];
};

/**
 * Report parameters shared by the Advanced Report form and the pinned/recent option modal,
 * so the two cannot validate differently. The caller owns the surrounding `<Form>`.
 */
export const ReportParamFields = ({
  showHermesId = false,
  showShareInHorizonOrganization = false,
  showCostRates = false,
  showDateFilter = false,
  shareInHorizonOrganizations = [],
}: Props) => {
  const { t } = useTranslation();

  return (
    <>
      {showHermesId ? (
        <Form.Item
          name="hermesId"
          label={t('reports.hermesInspectionId', 'Hermes Inspection ID')}
          rules={[
            {
              required: true,
              message: t('reports.hermesIdRequired', 'Please enter hermes inspection id.'),
            },
          ]}
        >
          <Input placeholder={t('reports.enterInspectionId', 'Enter inspection ID')} />
        </Form.Item>
      ) : undefined}
      {showShareInHorizonOrganization ? (
        <Form.Item
          name="shareInHorizonOrganizationId"
          label={t('reports.organization', 'Organization')}
          rules={[
            {
              required: true,
              message: t('reports.organizationRequired', 'Please select an Organization.'),
            },
          ]}
        >
          <Select placeholder={t('reports.selectOrganization', 'Select Organization')}>
            {shareInHorizonOrganizations.map(({ id, label }) => (
              <Option key={id} value={id}>
                {label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ) : undefined}
      {showCostRates ? (
        <>
          <Form.Item
            name="weatherStandbyRate"
            label={t('reports.standbyRateWeather', 'Standby Rate - Weather')}
            rules={[
              {
                required: true,
                message: t(
                  'reports.standbyRateWeatherRequired',
                  'Please enter Standby Rate - Weather.'
                ),
              },
              {
                type: 'number',
                min: 0,
                message: t(
                  'reports.standbyRateWeatherMin',
                  'Standby Rate - Weather cannot be less than zero.'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('reports.enterStandbyRateWeather', 'Enter Standby Rate - Weather')}
            />
          </Form.Item>
          <Form.Item
            name="siteStandbyRate"
            label={t('reports.standbyRateSite', 'Standby Rate - Site')}
            rules={[
              {
                required: true,
                message: t('reports.standbyRateSiteRequired', 'Please enter Standby Rate - Site.'),
              },
              {
                type: 'number',
                min: 0,
                message: t(
                  'reports.standbyRateSiteMin',
                  'Standby Rate - Site cannot be less than zero.'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('reports.enterStandbyRateSite', 'Enter Standby Rate - Site')}
            />
          </Form.Item>
          <Form.Item
            name="dailyBillableDelayHours"
            label={t('reports.billableDelayHours', 'Billable Delay Hours Per Day')}
            rules={[
              {
                required: true,
                message: t(
                  'reports.billableDelayHoursRequired',
                  'Please enter Billable Delay Hours Per Day.'
                ),
              },
              {
                type: 'number',
                min: 0,
                message: t(
                  'reports.billableDelayHoursMin',
                  'Billable Delay Hours Per Day cannot be less than zero.'
                ),
              },
              {
                type: 'number',
                max: 24,
                message: t(
                  'reports.billableDelayHoursMax',
                  'Billable Delay Hours Per Day cannot be more than 24.'
                ),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t(
                'reports.enterBillableDelayHours',
                'Enter Billable Delay Hours Per Day'
              )}
            />
          </Form.Item>
        </>
      ) : undefined}
      {showDateFilter ? (
        <Form.Item name="dateFilter" label={t('forms.date', 'Date')}>
          <DatePicker name="dateFilter" format="DD-MM-YYYY" style={{ width: '100%' }} />
        </Form.Item>
      ) : undefined}
    </>
  );
};
